"use client";

import { DirectionProvider as RadixDirectionProvider } from "@radix-ui/react-direction";
import { getCookie, setCookie } from "cookies-next";
import { NextIntlClientProvider } from "next-intl";
import {
	createContext,
	type PropsWithChildren,
	useContext,
	useEffect,
	useState,
} from "react";
import { setLanguageCookie } from "@/actions/setLanguage";
import { I18N_DEFAULT_LANGUAGE, I18N_LANGUAGES } from "@/config/i18n.config";
import { CookieType } from "@/constants";
import type { I18nProviderProps, Language } from "@/types/i18n";

const TranslationsContext = createContext<I18nProviderProps>({
	currentLanguage: I18N_DEFAULT_LANGUAGE,
	changeLanguage: () => {},
	isRTL: false,
});

export const useLanguage = () => useContext(TranslationsContext);

export const I18nProvider = ({ children }: PropsWithChildren) => {
	const [currentLanguage, setCurrentLanguage] = useState<Language>(
		I18N_DEFAULT_LANGUAGE,
	);
	const [messages, setMessages] = useState<Record<string, string>>({});

	// Load initial language (cookie > default)
	useEffect(() => {
		const getInitialLanguage = (): Language => {
			const cookieLangCode = getCookie(CookieType.LANG);
			if (cookieLangCode) {
				const match = I18N_LANGUAGES.find((l) => l.code === cookieLangCode);
				if (match) return match;
			}
			return I18N_DEFAULT_LANGUAGE;
		};

		setCurrentLanguage(getInitialLanguage());
	}, []);

	// Load messages & persist language in cookie
	useEffect(() => {
		let isMounted = true;

		const loadMessages = async () => {
			const msgs = (await import(`@/i18n/${currentLanguage.code}.ts`)).default;
			if (isMounted) {
				setMessages(msgs);
			}
		};
		loadMessages();

		document.documentElement.setAttribute("dir", currentLanguage.direction);
		setCookie(CookieType.LANG, currentLanguage.code);

		return () => {
			isMounted = false;
		};
	}, [currentLanguage]);

	const changeLanguage = async (language: Language) => {
		setCurrentLanguage(language);
		setCookie(CookieType.LANG, language.code);
		await setLanguageCookie(language.code);
	};

	const isRTL = currentLanguage.direction === "rtl";

	return (
		<TranslationsContext.Provider
			value={{
				isRTL,
				currentLanguage,
				changeLanguage,
			}}
		>
			{Object.keys(messages).length > 0 && (
				<NextIntlClientProvider
					locale={currentLanguage.code}
					messages={messages}
				>
					<RadixDirectionProvider dir={currentLanguage.direction}>
						{children}
					</RadixDirectionProvider>
				</NextIntlClientProvider>
			)}
		</TranslationsContext.Provider>
	);
};
